// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useAuthStore } from '@/store/authStore';
import { fetchResolvedTheme, saveUserPref, saveUserTheme } from '@/api/themeService';
import { fetchFormTheme, type FormThemeOverrides } from '@/api/formThemeService';
import { markayaCek } from './markaRengi';
import { ElpoNavyTurquoise } from './presets';
import { COLOR_VAR_MAP, DENSITY_BASE_SIZE, mergeTheme } from './types';
import { ensureContrast, isDarkColor } from './contrast';
import type { ColorMode, ThemeColors, ThemeMetadata } from './types';

// Varsayılan tema (tema seçilmemişse): Elpo kurumsal — derin lacivert menü, temiz açık içerik, turkuaz aksan.
const ThemeBase = ElpoNavyTurquoise;

interface ThemeContextValue {
  theme: ThemeMetadata;
  colorMode: ColorMode;
  effectiveMode: 'light' | 'dark';
  setColorMode: (mode: ColorMode) => void;
  /** Kullanicinin sectigi temayi kendi tercihine kaydet (null => tenant temasina don). Cortex tarzi. */
  setUserTheme: (theme: ThemeMetadata | null) => Promise<void>;
  reload: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const loadedFonts = new Set<string>();

async function loadFontFamily(family: string): Promise<void> {
  const normalized = family.toLowerCase().replace(/\s+/g, '-');
  if (loadedFonts.has(normalized)) return;
  loadedFonts.add(normalized);
  try {
    switch (normalized) {
      case 'inter':
        await import('@fontsource/inter/400.css');
        await import('@fontsource/inter/500.css');
        await import('@fontsource/inter/600.css');
        break;
      case 'outfit':
        await import('@fontsource/outfit/400.css');
        await import('@fontsource/outfit/500.css');
        await import('@fontsource/outfit/600.css');
        break;
      case 'poppins':
        await import('@fontsource/poppins/400.css');
        await import('@fontsource/poppins/500.css');
        await import('@fontsource/poppins/600.css');
        break;
      case 'nunito':
        await import('@fontsource/nunito/400.css');
        await import('@fontsource/nunito/500.css');
        await import('@fontsource/nunito/600.css');
        break;
      case 'ibm-plex-sans':
        await import('@fontsource/ibm-plex-sans/400.css');
        await import('@fontsource/ibm-plex-sans/500.css');
        await import('@fontsource/ibm-plex-sans/600.css');
        break;
    }
  } catch {
    // font paketi yüklü değilse sessizce geç
  }
}

// Yardimci/ikincil metin tokenlari — palet ne olursa olsun zemine gore kontrast tabanindan gecer.
// Yardimci metin cogu yerde 11-12px kullaniliyor; WCAG'in 4.5 alt siniri bu boyutta hala silik
// duruyordu (kullanici defalarca soyledi) — bu yuzden AAA bandi (7:1) hedefleniyor.
const READABLE_ON: Partial<Record<keyof ThemeColors, { surface: keyof ThemeColors; ratio: number }>> = {
  mutedForeground: { surface: 'background', ratio: 7 },
  secondaryForeground: { surface: 'secondary', ratio: 5.5 },
  accentForeground: { surface: 'accent', ratio: 5.5 },
  sidebarForeground: { surface: 'sidebar', ratio: 5.5 },
};

function applyColors(colors: ThemeColors | undefined): void {
  if (!colors) return;
  const root = document.documentElement;
  for (const [key, cssVar] of Object.entries(COLOR_VAR_MAP) as Array<[keyof ThemeColors, string]>) {
    // Kayıtlı temada mor kalmışsa ekrana çıkmadan markaya çevrilir. Kod temizlense de
    // veritabanındaki eski kayıt mor menü gösterebiliyor: dev veritabanında tenant teması
    // "Elpo Default" adıyla duruyordu ama menüsü #1d0e5c (koyu mor) idi.
    const ham = colors[key];
    const value = ham ? markayaCek(ham) : ham;
    if (!value) continue;
    const rule = READABLE_ON[key];
    if (!rule) {
      root.style.setProperty(cssVar, value);
      continue;
    }
    const surface = colors[rule.surface] ?? colors.background;
    root.style.setProperty(cssVar, surface ? ensureContrast(value, surface, rule.ratio) : value);
  }
}

// Koyu palet gercekten koyu mu? "Var mi" yetmiyor: Tema Studyosu acik icerik rengini koyu
// palete AYNEN kopyalayabiliyor (dev'de olculdu: colors.card = colorsDark.card = #e9fbf9).
// Oyle bir palet uygulanirsa acik zemin + koyu-tema kurallari cakisir, metin okunmaz olur.
function gercektenKoyu(colors: ThemeColors | undefined): boolean {
  if (!colors) return false;
  const yuzey = colors.background ?? colors.card;
  return yuzey ? isDarkColor(yuzey) : false;
}

function resetColorVars(): void {
  const root = document.documentElement;
  for (const cssVar of Object.values(COLOR_VAR_MAP)) {
    root.style.removeProperty(cssVar);
  }
}

function applyTheme(theme: ThemeMetadata, mode: 'light' | 'dark'): void {
  const root = document.documentElement;
  resetColorVars();
  if (theme.radius) root.style.setProperty('--radius', theme.radius);
  if (theme.density) {
    root.style.setProperty('--font-size', DENSITY_BASE_SIZE[theme.density]);
  } else if (theme.font?.baseSize) {
    root.style.setProperty('--font-size', theme.font.baseSize);
  }
  if (theme.font?.family) {
    root.style.setProperty('--font-sans', `'${theme.font.family}', -apple-system, BlinkMacSystemFont, sans-serif`);
    document.body.style.fontFamily = `'${theme.font.family}', -apple-system, BlinkMacSystemFont, sans-serif`;
    void loadFontFamily(theme.font.family);
  }
  if (theme.sidebar?.width) {
    root.style.setProperty('--sidebar-width', `${theme.sidebar.width}px`);
  }
  // Koyu modda temanin KOYU paleti yoksa acik renkler (kart/zemin) YAZILMAZ: styles.css'teki
  // .dark taban tokenlari gecerli kalir. Onceden acik palet yazilip ustune `dark` sinifi
  // ekleniyordu; acik zemin ile koyu-tema kurallari cakisiyor, tablo basligi siyah bantta
  // kaliyor ve hucre metni okunmuyordu (2026-08-05 dev'de olculdu: html.dark iken
  // --card #eafaf2 / --background #deeee6, hucre rengi rgb(166,173,200)).
  const koyuPaletGecerli = mode === 'dark' && gercektenKoyu(theme.colorsDark);
  if (mode === 'dark' && !koyuPaletGecerli) {
    root.classList.add('dark');
  } else {
    applyColors(theme.colors);
    if (koyuPaletGecerli) {
      applyColors(theme.colorsDark);
    }
    root.classList.toggle('dark', mode === 'dark');
  }
  // Görünüm derisi: classic seçiliyse body.skin-classic → klasik CSS yalnız bu kapsamda geçerli
  // (modern görünüm hiç etkilenmez). Varsayılan modern.
  document.body.classList.toggle('skin-classic', theme.skin === 'classic');
}

const FORM_THEME_VAR_MAP: Record<keyof FormThemeOverrides, string> = {
  inputRadius: '--form-input-radius',
  inputPaddingX: '--form-input-padding-x',
  inputPaddingY: '--form-input-padding-y',
  inputFontSize: '--form-input-font-size',
  inputShadow: '--form-input-shadow',
  labelSize: '--form-label-size',
  labelWeight: '--form-label-weight',
  labelGap: '--form-label-gap',
  sectionBorder: '--form-section-border',
  gap: '--form-gap',
  fontFamily: '--form-font-family',
};

const PRESET_DEFAULTS: Record<string, FormThemeOverrides> = {
  cortex: {
    inputRadius: '12px', inputPaddingX: '14px', inputPaddingY: '10px',
    inputFontSize: '13px', inputShadow: '0 1px 2px rgba(0,0,0,0.05)',
    labelSize: '12px', labelWeight: '600', labelGap: '6px',
    sectionBorder: '2px solid var(--primary)', gap: '16px',
  },
  modern: {
    inputRadius: '6px', inputPaddingX: '12px', inputPaddingY: '9px',
    inputFontSize: '13.5px', inputShadow: '0 1px 3px rgba(0,0,0,0.08)',
    labelSize: '12.5px', labelWeight: '500', labelGap: '5px',
    sectionBorder: '1px solid var(--border)', gap: '14px',
    fontFamily: 'IBM Plex Sans',
  },
};

function applyFormThemeVars(preset: string, overrides: FormThemeOverrides | null): void {
  const root = document.documentElement;
  const defaults = PRESET_DEFAULTS[preset] ?? PRESET_DEFAULTS.cortex;
  const merged = { ...defaults, ...overrides };
  for (const [key, cssVar] of Object.entries(FORM_THEME_VAR_MAP) as Array<[keyof FormThemeOverrides, string]>) {
    const val = merged[key];
    if (val) root.style.setProperty(cssVar, val);
  }
}

function resolveMode(colorMode: ColorMode): 'light' | 'dark' {
  if (colorMode === 'auto') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return colorMode;
}

interface ProviderProps {
  readonly children: ReactNode;
}

export function ThemeProvider({ children }: ProviderProps): ReactElement {
  const [theme, setTheme] = useState<ThemeMetadata>(ThemeBase);
  const [colorMode, setColorModeState] = useState<ColorMode>(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('elpo_color_mode') : null;
    return (saved === 'dark' || saved === 'auto' || saved === 'light') ? saved : 'light';
  });
  const loadedRef = useRef(false);
  const token = useAuthStore((s) => s.token);

  const reload = useCallback(async () => {
    try {
      const resolved = await fetchResolvedTheme();
      let merged: ThemeMetadata = ThemeBase;
      const platform = parseJson(resolved.platformJson);
      if (platform) merged = mergeTheme(merged, platform);
      const tenant = parseJson(resolved.tenantJson);
      if (tenant) merged = mergeTheme(merged, tenant);
      // Kullanici teması en üstte (Cortex tarzi: her kullanici kendi temasini secer)
      const userTheme = parseJson(resolved.userJson);
      if (userTheme) merged = mergeTheme(merged, userTheme);
      setTheme(merged);
      if (resolved.colorMode === 'dark' || resolved.colorMode === 'auto' || resolved.colorMode === 'light') {
        setColorModeState(resolved.colorMode);
        localStorage.setItem('elpo_color_mode', resolved.colorMode);
      }
    } catch {
      setTheme(ThemeBase);
    }
    try {
      const formTheme = await fetchFormTheme();
      applyFormThemeVars(formTheme.preset, formTheme.overrides);
    } catch {
      applyFormThemeVars('cortex', null);
    }
  }, []);

  useEffect(() => {
    if (!token) { setTheme(ThemeBase); loadedRef.current = false; return; }
    if (loadedRef.current) return;
    loadedRef.current = true;
    void reload();
  }, [token, reload]);

  const effectiveMode = useMemo(() => resolveMode(colorMode), [colorMode]);

  useEffect(() => {
    applyTheme(theme, effectiveMode);
  }, [theme, effectiveMode]);

  useEffect(() => {
    if (colorMode !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme(theme, mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [colorMode, theme]);

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode);
    localStorage.setItem('elpo_color_mode', mode);
    if (token) { void saveUserPref(mode).catch(() => {}); }
  }, [token]);

  const setUserTheme = useCallback(async (next: ThemeMetadata | null) => {
    // Optimistik: secilen temayi HEMEN uygula. reload() ÇAĞIRMA — backend round-trip flicker/race
    // yaratıyordu (seçim bir an uygulanıp eski haline dönüyordu). Kayıt arka planda; optimistik state doğru.
    if (next) {
      setTheme(mergeTheme(ThemeBase, next));
    } else {
      // null => kullanıcı tercihini sil, tenant/varsayılana dön. Bunda backend'den taze cascade gerekir.
      await saveUserTheme(null).catch(() => {});
      await reload();
      return;
    }
    void saveUserTheme(next).catch(() => {});
  }, [reload]);

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    colorMode,
    effectiveMode,
    setColorMode,
    setUserTheme,
    reload,
  }), [theme, colorMode, effectiveMode, setColorMode, setUserTheme, reload]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}

function parseJson(json: string): ThemeMetadata | null {
  if (!json || json === '{}') return null;
  try { return JSON.parse(json) as ThemeMetadata; } catch { return null; }
}
